/**
 * EN-1516: display, permission and validation helpers for the Download
 * Manager's queued commands list. Pure functions (no store, no HTTP) so they
 * can be unit tested; e2e/queued-command-display.unit.spec.ts covers them.
 */
import type { IUser } from '../models/User';
import type {
  ICancelDownloadCommandResult,
  ICancelDownloadCommandsResponse,
  IQueuedCommand,
  IUpdateCommandRangeError,
  IUpdateCommandRangeRequest,
} from '../models/QueuedCommand';
import {
  RFD_STOP_DOWNLOADS_FILE_TYPE,
  STOP_DOWNLOADS_LABEL,
  MISSING_RANGE_PLACEHOLDER,
} from './DownloadRangeCommandDisplay';
import {
  DOWNLOAD_OPTION_PASSAGES,
  DOWNLOAD_OPTION_RAW,
  LEGACY_RAW_DOWNLOAD_RANGE_MINUTES,
  RAW_DOWNLOAD_RANGE_MINUTES,
  formatDownloadPickerUtc,
} from './DownloadRangeDefaults';

/**
 * Product decisions still to be confirmed by Mark (EN-1516). Change them here.
 *
 * - showStopRows: list the automatic RFD FileType 99 stop rows. Sent to the
 *   API as `includeStopRows`.
 * - cancelDownloadsOnly: offer Cancel only on downloads (passages / raw);
 *   Dave's EN-1515 endpoint cancels downloads only, so other commands would
 *   come back NotDownload anyway.
 * - editOnlyWhileOffline: offer Edit only while the device is offline and the
 *   command unsent. The server enforces this regardless (409 DeviceConnected /
 *   AlreadySent); turning it off here would only show a button that fails.
 */
export const QUEUED_COMMANDS_POLICY = {
  showStopRows: false,
  cancelDownloadsOnly: true,
  editOnlyWhileOffline: true,
};

/** Roles that may edit or cancel, the same gate as queueing (DeviceInfo.vue). */
export const QUEUED_COMMAND_ADMIN_ROLES = ['admin', 'superadmin'];

/**
 * The user's role as the queueing gate reads it: DeviceInfo.vue shows the
 * Commands button only when `$store.state.user?.role` (the highest-priority
 * role, User.role = roles[0]) is 'superadmin' or 'admin'. A plain object
 * from localStorage may carry only `roles`.
 */
export function queueingRole(user: IUser | null | undefined): string {
  if (!user) return '';
  const legacy = (user as unknown as Record<string, unknown>)['role'];
  if (typeof legacy === 'string') return legacy;
  return user.roles?.[0] ?? '';
}

export const PASSAGES_DOWNLOAD_LABEL = 'Passages download';
export const RAW_DOWNLOAD_LABEL = 'Raw download';

/** 'RAW - 2 Mins Max' (firmware 10+, and when the firmware is unknown). */
export const RAW_MAX_RANGE_SECONDS = RAW_DOWNLOAD_RANGE_MINUTES * 60;
/** 'RAW - 5 Mins Max' (firmware below 10). */
export const LEGACY_RAW_MAX_RANGE_SECONDS =
  LEGACY_RAW_DOWNLOAD_RANGE_MINUTES * 60;

export const NOT_ADMIN_MESSAGE =
  'Only administrators can edit or cancel queued commands.';
export const NO_GROUP_MESSAGE =
  'Your account is not assigned to a group, so queued commands cannot be shown.';

/**
 * True when the user may edit or cancel queued commands: the same rule as
 * queueing, and as Chad's EN-1517 `canQueueDownloads(role)`
 * (utils/DownloadSessionAccess.ts, not on main yet).
 * TODO(EN-1517): once DownloadSessionAccess.ts is on main, call
 * canQueueDownloads(queueingRole(user)) here so there is one gate.
 */
export function canManageQueuedCommands(
  user: IUser | null | undefined
): boolean {
  return QUEUED_COMMAND_ADMIN_ROLES.includes(queueingRole(user));
}

export function canEditQueuedCommand(
  command: IQueuedCommand,
  isAdmin: boolean
): boolean {
  if (!isAdmin || !command.isDownload || command.isStopRow) return false;
  if (command.transport === 'Iridium') return false;
  if (!QUEUED_COMMANDS_POLICY.editOnlyWhileOffline) return true;
  return command.isEditable === true;
}

export function canCancelQueuedCommand(
  command: IQueuedCommand,
  isAdmin: boolean
): boolean {
  if (!isAdmin || command.isStopRow) return false;
  if (QUEUED_COMMANDS_POLICY.cancelDownloadsOnly && !command.isDownload)
    return false;
  return command.isCancellable === true;
}

/**
 * Parses the API's 'yyyy-MM-dd HH:mm:ss.ffffff' (UTC, no zone) or an ISO
 * string into a Date. Returns null when it cannot be parsed.
 */
export function parseApiUtc(value: string | null | undefined): Date | null {
  if (!value) return null;
  const m =
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?(Z|[+-]\d{2}:?\d{2})?$/.exec(
      value.trim()
    );
  if (!m) return null;
  if (m[8]) {
    const parsed = new Date(value.trim().replace(' ', 'T'));
    return isNaN(parsed.getTime()) ? null : parsed;
  }
  const ms = m[7] ? Number((m[7] + '000').substring(0, 3)) : 0;
  return new Date(
    Date.UTC(
      Number(m[1]),
      Number(m[2]) - 1,
      Number(m[3]),
      Number(m[4]),
      Number(m[5]),
      m[6] ? Number(m[6]) : 0,
      ms
    )
  );
}

/** API date to the pickers' 'YYYY-MM-DD HH:mm:ss' UTC string ('' if invalid). */
export function apiUtcToPicker(value: string | null | undefined): string {
  const date = parseApiUtc(value);
  return date ? formatDownloadPickerUtc(date) : '';
}

export function queuedCommandFileType(command: IQueuedCommand): number | null {
  const fileType = command.parameters?.FileType;
  if (fileType === null || fileType === undefined) return null;
  const n = Number(fileType);
  return isNaN(n) ? null : n;
}

/** Type column label. */
export function queuedCommandTypeLabel(command: IQueuedCommand): string {
  const fileType = queuedCommandFileType(command);
  if (command.isStopRow || fileType === RFD_STOP_DOWNLOADS_FILE_TYPE)
    return STOP_DOWNLOADS_LABEL;
  if (command.isDownload) {
    if (fileType === DOWNLOAD_OPTION_RAW) return RAW_DOWNLOAD_LABEL;
    if (fileType === DOWNLOAD_OPTION_PASSAGES) return PASSAGES_DOWNLOAD_LABEL;
  }
  return command.commandType || command.command || 'Command';
}

/** Range column text: 'YYYY-MM-DD HH:mm:ss – HH:mm:ss' (UTC), or a dash. */
export function queuedCommandRangeText(command: IQueuedCommand): string {
  if (!command.isDownload) return '';
  const start = parseApiUtc(command.parameters?.FileNamesStartDate);
  const end = parseApiUtc(command.parameters?.FileNamesEndDate);
  if (!start || !end) return MISSING_RANGE_PLACEHOLDER;
  const a = formatDownloadPickerUtc(start);
  const b = formatDownloadPickerUtc(end);
  // Same day: show the end as time only.
  return a.substring(0, 10) === b.substring(0, 10)
    ? a + ' – ' + b.substring(11)
    : a + ' – ' + b;
}

/** 'YYYY-MM-DD HH:mm:ss' (UTC) of when the command was queued, or ''. */
export function queuedAtText(command: IQueuedCommand): string {
  return apiUtcToPicker(command.timeStampSent);
}

export function queuedCommandStatusLabel(command: IQueuedCommand): string {
  switch (command.status) {
    case 'Sending':
      return 'Sending';
    case 'Queued':
      return 'Queued (AGM online)';
    case 'Waiting':
      return 'Waiting for AGM';
    default:
      return String(command.status ?? '');
  }
}

export function queuedCommandStatusColor(command: IQueuedCommand): string {
  switch (command.status) {
    case 'Sending':
      return 'positive';
    case 'Queued':
      return 'blue-brand';
    default:
      return 'grey-7';
  }
}

/** Why Edit is unavailable (tooltip), or '' when it is available. */
export function editUnavailableReason(command: IQueuedCommand): string {
  if (!command.isDownload || command.isStopRow)
    return 'Only passages and raw downloads can be edited.';
  if (command.transport === 'Iridium')
    return 'Iridium commands cannot be edited.';
  if (command.status === 'Sending')
    return 'Already sent to the AGM. Cancel it instead.';
  if (command.deviceConnected)
    return 'The AGM is online, so the download may be sent at any moment. It can be edited only while the AGM is offline.';
  if (!command.isEditable) return 'This command cannot be edited.';
  return '';
}

/** RAW cap for a device; firmwareVersion is e.g. 10.71 (undefined = unknown). */
export function rawMaxRangeSeconds(firmwareVersion?: number | null): number {
  if (firmwareVersion === null || firmwareVersion === undefined)
    return RAW_MAX_RANGE_SECONDS;
  const major = Math.floor(Number(firmwareVersion));
  return major >= 1 && major < 10
    ? LEGACY_RAW_MAX_RANGE_SECONDS
    : RAW_MAX_RANGE_SECONDS;
}

function formatCap(seconds: number): string {
  if (seconds % 60 === 0) {
    const minutes = seconds / 60;
    return minutes === 2
      ? 'two minute'
      : minutes === 5
      ? 'five minute'
      : minutes + ' minute';
  }
  return seconds + ' second';
}

export interface RangeValidation {
  start?: Date;
  end?: Date;
  error?: string;
}

/**
 * Validates picker strings ('YYYY-MM-DD HH:mm:ss', UTC) for a range edit.
 * Mirrors the server's checks so most mistakes never leave the browser; the
 * server stays the authority.
 */
export function validateQueuedRange(
  startPicker: string,
  endPicker: string,
  fileType: number | null,
  firmwareVersion?: number | null
): RangeValidation {
  const start = parseApiUtc(startPicker);
  if (!start) return { error: 'The start date is not valid.' };
  const end = parseApiUtc(endPicker);
  if (!end) return { error: 'The end date is not valid.' };
  if (start.getTime() >= end.getTime())
    return { error: 'The start date must be before the end date.' };
  if (fileType === DOWNLOAD_OPTION_RAW) {
    const cap = rawMaxRangeSeconds(firmwareVersion);
    if ((end.getTime() - start.getTime()) / 1000 > cap) {
      return {
        error:
          'A raw data download request is limited to a ' +
          formatCap(cap) +
          ' maximum interval.',
      };
    }
  }
  return { start, end };
}

export function buildRangeUpdateRequest(
  start: Date,
  end: Date
): IUpdateCommandRangeRequest {
  return {
    fileNamesStartDate: start.toISOString(),
    fileNamesEndDate: end.toISOString(),
  };
}

const EDIT_REASON_MESSAGES: Record<string, string> = {
  AlreadyProcessed:
    'This command is no longer queued (it was delivered, cancelled or replaced).',
  AlreadySent: 'This download was already sent to the AGM.',
  DeviceConnected:
    'The AGM came online, so the download may be sent at any moment. It can be edited only while the AGM is offline.',
  NotEditable: 'This command can no longer be edited.',
};

/** User-facing message for a failed `PUT /commands/{id}/range`. */
export function editErrorMessage(
  status: number | undefined,
  body?: IUpdateCommandRangeError | null
): string {
  if (status === 403) return NOT_ADMIN_MESSAGE;
  if (status === 404) return 'This command is no longer queued.';
  if (status === 400 || status === 409) {
    if (body?.reason && status === 409 && EDIT_REASON_MESSAGES[body.reason])
      return EDIT_REASON_MESSAGES[body.reason];
    if (body?.message) return body.message;
    if (body?.reason && EDIT_REASON_MESSAGES[body.reason])
      return EDIT_REASON_MESSAGES[body.reason];
  }
  return 'The queued command could not be updated.';
}

/** Summary for the cancel result dialog. */
export function cancelResultSummary(
  response: ICancelDownloadCommandsResponse | null | undefined
): string {
  const results: ICancelDownloadCommandResult[] = response?.results ?? [];
  if (results.length === 0) return 'Nothing was cancelled.';
  const cancelled = response?.cancelledCount ?? 0;
  const others = results.filter(
    (r) => r.status !== 'Cancelled' && r.status !== 'AlreadyCancelled'
  );
  if (others.length === 0)
    return cancelled === 1
      ? 'The download was cancelled.'
      : cancelled + ' downloads were cancelled.';
  return others
    .map((r) => (r.message ? r.message : 'Not cancelled: ' + r.status))
    .join(' ');
}

/**
 * EN-1551: the list shows queued downloads only. `GET /commands/queued`
 * returns every unprocessed command for the group's devices (powerMode,
 * frequencySelect, ...); `isDownload` marks passage and raw downloads. This is
 * applied once, to the loaded rows, so the empty text, the "AGM UIDs" filter
 * and its "Showing X of Y" count all see downloads only.
 */
export function onlyDownloadCommands(
  commands: IQueuedCommand[]
): IQueuedCommand[] {
  return commands.filter((c) => c.isDownload === true);
}

/** Sorted by UID, then queued time, then CID (the API returns newest first). */
export function sortQueuedCommands(
  commands: IQueuedCommand[]
): IQueuedCommand[] {
  return [...commands].sort(
    (a, b) =>
      a.uid - b.uid ||
      (parseApiUtc(a.timeStampSent)?.getTime() ?? 0) -
        (parseApiUtc(b.timeStampSent)?.getTime() ?? 0) ||
      a.commandId - b.commandId
  );
}

/**
 * Badge number for the 'Show download sessions' button from Chad's EN-1517
 * `downloadSessionCounts` getter, which (in his unmerged commit) returns
 * `{ queued, active, total, ... }`. The badge shows the in-flight cards
 * (queued + active). Not on main yet, so anything else, including undefined,
 * is handled: a plain number, `{ total }` alone, or a per-UID map.
 */
export function downloadSessionCountBadge(value: unknown): number | null {
  const positive = (n: number): number | null => (n > 0 ? n : null);
  if (typeof value === 'number') return positive(value);
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (typeof v.queued === 'number' || typeof v.active === 'number') {
    const queued = typeof v.queued === 'number' ? v.queued : 0;
    const active = typeof v.active === 'number' ? v.active : 0;
    return positive(queued + active);
  }
  if (typeof v.total === 'number') return positive(v.total);
  return positive(
    Object.values(v)
      .filter((n): n is number => typeof n === 'number')
      .reduce((a, b) => a + b, 0)
  );
}

/** Generic text when a cancel fails without a usable server message. */
export const CANCEL_FAILED_MESSAGE = 'The download could not be cancelled.';

/**
 * Body of the cancel confirmation. EN-1515 cancel sends nothing to the AGM
 * (Mark's decision): it marks the command (and its session) cancelled here,
 * so a Sending row must not promise that the AGM is told to stop.
 */
export function cancelConfirmText(command: IQueuedCommand): string {
  const range = queuedCommandRangeText(command);
  const what =
    queuedCommandTypeLabel(command) +
    ' for AGM UID ' +
    command.uid +
    (range ? ' (' + range + ' UTC)' : '');
  if (command.status === 'Sending')
    return (
      what +
      ' will be marked cancelled here. The AGM may already be sending data.'
    );
  return what + ' will be removed from the queue.';
}

/**
 * Message for a failed `POST /download-commands/cancel`. A 400 carries a
 * plain-string body from the EN-1515 endpoint, which is shown as is.
 */
export function cancelErrorMessage(
  status: number | undefined,
  body?: unknown
): string {
  if (status === 403) return NOT_ADMIN_MESSAGE;
  if (status === 400 && typeof body === 'string' && body.trim() !== '')
    return body.trim();
  return CANCEL_FAILED_MESSAGE;
}

/**
 * True when a Vuex action is registered. EN-1516 merges before EN-1517
 * (merge order Dave, Bill, Chad), so `openDownloadSessions` may not exist
 * yet; the "Show download sessions" buttons render only when it does.
 * Uses the store's `_actions` map (Vuex 3 and 4), read-only.
 */
export function hasStoreAction(store: unknown, name: string): boolean {
  const actions = (store as { _actions?: unknown } | null | undefined)
    ?._actions;
  return (
    !!actions &&
    typeof actions === 'object' &&
    Object.prototype.hasOwnProperty.call(actions, name)
  );
}

/** The EN-1517 action that opens the download sessions panel. */
export const OPEN_DOWNLOAD_SESSIONS_ACTION = 'openDownloadSessions';

/** Auto-refresh interval for the queued list while it is visible. */
export const QUEUED_COMMANDS_POLL_MS = 15000;

/**
 * Orders list loads so the newest request wins: `begin()` numbers a request,
 * `isLatest(id)` says whether its response may still be applied, and a load
 * asked for while one is in flight is queued (`requestAgain`) and run once
 * the current one finishes (`takeQueued`).
 */
export class LatestRequestGate {
  private latest = 0;
  private queued = false;
  inFlight = false;

  begin(): number {
    this.inFlight = true;
    this.latest += 1;
    return this.latest;
  }

  isLatest(id: number): boolean {
    return id === this.latest;
  }

  /** Marks a request finished; true when it was the latest one. */
  end(id: number): boolean {
    if (id === this.latest) this.inFlight = false;
    return id === this.latest;
  }

  requestAgain(): void {
    this.queued = true;
  }

  /** True (once) when a load was asked for while one was in flight. */
  takeQueued(): boolean {
    const queued = this.queued;
    this.queued = false;
    return queued;
  }

  /** Makes every in-flight response stale (e.g. when the list closes). */
  invalidate(): void {
    this.latest += 1;
    this.inFlight = false;
    this.queued = false;
  }
}
